import { useState, useEffect, useCallback, useMemo } from 'react'
import { Plus, Trash2, Loader2, RefreshCw } from 'lucide-react'
import { InlineEditField } from '@/components/InlineEditField'
import { businessService, FinancialYearRecord, CreateFinancialYearData } from '@/services/businessService'
import { aiService } from '@/services/aiService'
import { formatIndianCurrency } from '@/utils/formatters'
import { useToast } from '@/components/ui/MobileToast'

interface FinancialDataTabProps {
  business: any
  onUpdate: (updated: any) => void
}

/**
 * Generate available FY options from 2020-21 through current FY.
 */
function generateFYOptions(): { value: string; label: string }[] {
  const currentYear = new Date().getFullYear()
  const currentMonth = new Date().getMonth() // 0-indexed
  // Indian FY starts in April (month index 3)
  const currentFYEndYear = currentMonth >= 3 ? currentYear + 1 : currentYear
  const options: { value: string; label: string }[] = []

  for (let endYear = 2021; endYear <= currentFYEndYear; endYear++) {
    const startYear = endYear - 1
    const fy = `${startYear}-${String(endYear).slice(2)}`
    options.push({ value: fy, label: `FY ${fy}` })
  }

  return options.reverse() // Most recent first
}

export function FinancialDataTab({ business, onUpdate }: FinancialDataTabProps) {
  const { showToast } = useToast()
  const [records, setRecords] = useState<FinancialYearRecord[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isRecalculating, setIsRecalculating] = useState(false)
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null)

  const [formData, setFormData] = useState<CreateFinancialYearData>({
    financial_year: '',
    annual_turnover: 0,
    net_profit: 0,
    total_expenses: 0,
  })

  const allFYOptions = useMemo(() => generateFYOptions(), [])

  // Filter out existing FYs from dropdown
  const availableFYOptions = useMemo(() => {
    const existingFYs = new Set(records.map((r) => r.financial_year))
    return allFYOptions.filter((opt) => !existingFYs.has(opt.value))
  }, [allFYOptions, records])

  const loadRecords = useCallback(async () => {
    setIsLoading(true)
    try {
      const data = await businessService.getFinancialYears(business.id)
      setRecords(data)
    } catch (err) {
      showToast({
        type: 'error',
        title: 'Load Failed',
        message: 'Unable to load financial year records.',
      })
    } finally {
      setIsLoading(false)
    }
  }, [business.id, showToast])

  useEffect(() => {
    loadRecords()
  }, [loadRecords])

  const handleAddRecord = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.financial_year) {
      showToast({ type: 'error', title: 'Required', message: 'Please select a financial year.' })
      return
    }

    setIsSubmitting(true)
    try {
      const newRecord = await businessService.createFinancialYear(business.id, formData)
      // Reload the full list from API to ensure consistency
      await loadRecords()
      setShowForm(false)
      setFormData({ financial_year: '', annual_turnover: 0, net_profit: 0, total_expenses: 0 })

      // Check badge in response
      if ((newRecord as any).badge_awarded) {
        showToast({
          type: 'success',
          title: '🏆 Badge Earned!',
          message: `Congratulations! You earned the "${(newRecord as any).badge_awarded}" badge.`,
        })
      }

      // Prompt for score recalculation
      promptScoreRecalculation()
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.message || 'Failed to create record.'
      showToast({ type: 'error', title: 'Save Failed', message })
    } finally {
      setIsSubmitting(false)
    }
  }

  const promptScoreRecalculation = () => {
    if (window.confirm('Recalculate TLBH score with updated financial data?')) {
      recalculateScore()
    }
  }

  const recalculateScore = async () => {
    setIsRecalculating(true)
    try {
      const result = await aiService.getScore(business.id, true) // fresh=true for recalculation
      if (result.data) {
        onUpdate({
          ...business,
          score: result.data.score,
          band: result.data.risk_tier,
          confidence: result.data.confidence_interval
            ? ((result.data.confidence_interval.upper + result.data.confidence_interval.lower) / 2).toString()
            : business.confidence,
        })
        showToast({
          type: 'success',
          title: 'Score Updated',
          message: `New TLBH score: ${result.data.score.toFixed(2)}`,
        })
      }
    } catch (err) {
      showToast({
        type: 'error',
        title: 'Recalculation Failed',
        message: 'Unable to recalculate score. Previous values retained.',
      })
    } finally {
      setIsRecalculating(false)
    }
  }

  const handleInlineEdit = useCallback(
    async (fyId: string, fieldName: string, value: string | number) => {
      try {
        const updated = await businessService.updateFinancialYear(business.id, fyId, {
          [fieldName]: Number(value),
        })
        setRecords((prev) => prev.map((r) => (r.id === fyId ? updated : r)))

        if ((updated as any).badge_awarded) {
          showToast({
            type: 'success',
            title: '🏆 Badge Earned!',
            message: `Congratulations! You earned the "${(updated as any).badge_awarded}" badge.`,
          })
        }
      } catch (err: any) {
        const message = err?.response?.data?.error || err?.message || 'Save failed — please try again'
        throw new Error(message)
      }
    },
    [business.id, showToast]
  )

  const handleDelete = async (fyId: string) => {
    try {
      await businessService.deleteFinancialYear(business.id, fyId)
      setRecords((prev) => prev.filter((r) => r.id !== fyId))
      setDeleteConfirmId(null)
      showToast({ type: 'success', title: 'Deleted', message: 'Financial year record deleted.' })
    } catch (err) {
      showToast({ type: 'error', title: 'Delete Failed', message: 'Unable to delete record.' })
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
        <span className="ml-2 text-gray-600 dark:text-gray-400">Loading financial data...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header with actions */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Financial Year Records
        </h3>
        <div className="flex items-center gap-3">
          {isRecalculating && (
            <span className="flex items-center gap-1 text-sm text-primary-600">
              <RefreshCw className="w-4 h-4 animate-spin" />
              Recalculating...
            </span>
          )}
          <button
            onClick={() => setShowForm(true)}
            disabled={showForm || availableFYOptions.length === 0}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-4 h-4" />
            Add Financial Year
          </button>
        </div>
      </div>

      {/* Add FY Form */}
      {showForm && (
        <form
          onSubmit={handleAddRecord}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6"
        >
          <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">
            Add Financial Year Record
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Financial Year <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.financial_year}
                onChange={(e) => setFormData((prev) => ({ ...prev, financial_year: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                required
              >
                <option value="">Select FY...</option>
                {availableFYOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Annual Turnover (₹)
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={formData.annual_turnover}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, annual_turnover: parseFloat(e.target.value) || 0 }))
                }
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Net Profit (₹)
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.net_profit}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, net_profit: parseFloat(e.target.value) || 0 }))
                }
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Total Expenses (₹)
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={formData.total_expenses}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, total_expenses: parseFloat(e.target.value) || 0 }))
                }
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>

          <div className="flex items-center gap-3 mt-4">
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors disabled:opacity-50"
            >
              {isSubmitting ? 'Saving...' : 'Save Record'}
            </button>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Records list */}
      {records.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8 text-center">
          <p className="text-gray-500 dark:text-gray-400">
            No financial year records yet. Click "Add Financial Year" to get started.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {records.map((record) => (
            <div
              key={record.id}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-md font-semibold text-gray-900 dark:text-white">
                  FY {record.financial_year}
                </h4>
                <div className="flex items-center gap-2">
                  {deleteConfirmId === record.id ? (
                    <>
                      <span className="text-sm text-red-600">Delete this record?</span>
                      <button
                        onClick={() => handleDelete(record.id)}
                        className="px-2 py-1 text-xs text-white bg-red-600 hover:bg-red-700 rounded transition-colors"
                      >
                        Confirm
                      </button>
                      <button
                        onClick={() => setDeleteConfirmId(null)}
                        className="px-2 py-1 text-xs text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => setDeleteConfirmId(record.id)}
                      className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                      aria-label="Delete record"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <InlineEditField
                    label="Annual Turnover"
                    value={record.annual_turnover}
                    fieldName="annual_turnover"
                    type="number"
                    onSave={(fieldName, value) => handleInlineEdit(record.id, fieldName, value)}
                    placeholder="0"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    {formatIndianCurrency(record.annual_turnover)}
                  </p>
                </div>

                <div>
                  <InlineEditField
                    label="Net Profit"
                    value={record.net_profit}
                    fieldName="net_profit"
                    type="number"
                    onSave={(fieldName, value) => handleInlineEdit(record.id, fieldName, value)}
                    placeholder="0"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    {formatIndianCurrency(record.net_profit)}
                  </p>
                </div>

                <div>
                  <InlineEditField
                    label="Total Expenses"
                    value={record.total_expenses}
                    fieldName="total_expenses"
                    type="number"
                    onSave={(fieldName, value) => handleInlineEdit(record.id, fieldName, value)}
                    placeholder="0"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    {formatIndianCurrency(record.total_expenses)}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
